import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import BranchLayout from '../layouts/BranchLayout';
import { useAuth } from '../contexts/AuthContext';
import AuthGuard from '../contexts/AuthGuard';

const Dashboard = lazy(() => import('../pages/modules/branch/dashboard/Dashboard'));
const KitchenManagement = lazy(() => import('../pages/modules/branch/user-management/KitchenManagement/KitchenManagement'));
const DeliveryManagement = lazy(() => import('../pages/modules/branch/user-management/DeliveryManagement/DeliveryManagement'));
const CashierManagement = lazy(() => import('../pages/modules/branch/user-management/CashierManagement/CashierManagement'));
const CaptainManagement = lazy(() => import('../pages/modules/branch/user-management/CaptainManagement/CaptainManagement'));
const CustomerManagement = lazy(() => import('../pages/modules/branch/user-management/CustomerManagement/CustomerManagement'));
const MenuTree = lazy(() => import('../pages/modules/branch/menu-management/MenuTree/MenuTree'));
const MenuCategory = lazy(() => import('../pages/modules/branch/menu-management/MenuCategory/MenuCategory'));
const MenuSubcategory = lazy(() => import('../pages/modules/branch/menu-management/MenuSubcategory/MenuSubcategory'));
const Section = lazy(() => import('../pages/modules/branch/menu-management/Section/Section'));
const DiningTables = lazy(() => import('../pages/modules/branch/menu-management/DiningTables/DiningTables'));
const MenuItems = lazy(() => import('../pages/modules/branch/menu-management/MenuItems/MenuItems'));
const Addons = lazy(() => import('../pages/modules/branch/menu-management/Addons/Addons'));
const DeliveryZones = lazy(() => import('../pages/modules/branch/menu-management/DeliveryZones/DeliveryZones'));
const RestaurantHours = lazy(() => import('../pages/modules/branch/menu-management/RestaurantHours/RestaurantHours'));

// Order Management Components
const OrderList = lazy(() => import('../pages/modules/restaurant/orders/OrderList/OrderList'));

// Settings Components
const BankDetail = lazy(() => import('../pages/modules/branch/settings/BankDetail/BankDetail'));

// Outstanding Components
const OutstandingDelivery = lazy(() => import('../pages/modules/branch/outstanding/Delivery/OutstandingDelivery'));
const OutstandingHistory = lazy(() => import('../pages/modules/branch/outstanding/OutstandingHistory/OutstandingHistory'));

// Wallet Topup
const WalletTopupRequest = lazy(() => import('../pages/modules/branch/wallet-topup-request/WalletTopupRequest'));
const TopupHistory = lazy(() => import('../pages/modules/branch/wallet-topup-request/TopupHistory'));
const NewOrders = lazy(() => import('../pages/modules/branch/orders/NewOrders/NewOrders'));
const Preparing = lazy(() => import('../pages/modules/branch/orders/Preparing/Preparing'));
const Onway = lazy(() => import('../pages/modules/branch/orders/Onway/Onway'));
const Delivered = lazy(() => import('../pages/modules/branch/orders/Delivered/Delivered'));
const CancelledOrders = lazy(() => import('../pages/modules/branch/orders/CancelledOrders/CancelledOrders'));

// Shared operations pages
const TablesManagement = lazy(() => import('../pages/modules/shared/TablesManagement/TablesManagement'));
const CustomerList = lazy(() => import('../pages/modules/shared/CustomerList/CustomerList'));

const RouteFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
  </div>
);

const BranchRoutes = () => {
  const { logout } = useAuth();

  return (
    <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<BranchLayout onLogout={logout} />}>
          <Route path="/" element={<Navigate to="/branch/dashboard" replace />} />
          <Route path="/branch" element={<Navigate to="/branch/dashboard" replace />} />

          {/* Branch Module Routes */}
          <Route path="/branch/dashboard" element={<Dashboard />} />
          <Route path="/branch/user-management/kitchen" element={<KitchenManagement />} />
          <Route path="/branch/user-management/delivery" element={<DeliveryManagement />} />
          <Route path="/branch/user-management/captain" element={<CaptainManagement />} />
          <Route path="/branch/user-management/cashier" element={<CashierManagement />} />
          <Route path="/branch/user-management/customers" element={<CustomerManagement />} />
          <Route path="/branch/menu-management/menu-tree" element={<MenuTree />} />
          <Route path="/branch/menu-management/categories" element={<MenuCategory />} />
          <Route path="/branch/menu-management/subcategories" element={<MenuSubcategory />} />
          <Route path="/branch/menu-management/sections" element={<Section />} />
          <Route path="/branch/menu-management/dining-tables" element={<DiningTables />} />
          <Route path="/branch/menu-management/items" element={<MenuItems />} />
          <Route path="/branch/menu-management/addons" element={<Addons />} />
          <Route path="/branch/menu-management/delivery-zones" element={<DeliveryZones />} />
          <Route path="/branch/menu-management/restaurant-hours" element={<RestaurantHours />} />

          {/* Order Management Routes */}
          <Route path="/branch/orders/list" element={<OrderList />} />
          <Route path="/branch/orders/new" element={<NewOrders />} />
          <Route path="/branch/orders/preparing" element={<Preparing />} />
          <Route path="/branch/orders/onway" element={<Onway />} />
          <Route path="/branch/orders/delivered" element={<Delivered />} />
          <Route path="/branch/orders/cancelled" element={<CancelledOrders />} />

          {/* Outstanding Routes */}
          <Route path="/branch/outstanding/delivery" element={<OutstandingDelivery />} />
          <Route path="/branch/outstanding/history" element={<OutstandingHistory />} />

          {/* Settings Routes */}
          <Route path="/branch/settings/bank-details" element={<BankDetail />} />

          {/* Wallet Topup */}
          <Route path="/branch/wallet-topup/requests" element={<WalletTopupRequest />} />
          <Route path="/branch/wallet-topup/history" element={<TopupHistory />} />

          {/* Operations — shared */}
          <Route path="/branch/operations/tables" element={<TablesManagement scope="branch" />} />
          <Route path="/branch/operations/customers" element={<CustomerList scope="branch" />} />

          <Route path="*" element={<Navigate to="/branch/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
    </AuthGuard>
  );
};

export default BranchRoutes;
